import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloudOff, Download, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import { logout } from '../../lib/api';
import { useIsLocalMode, useOfflineStore } from '../../lib/offline/state';

const INSTALL_DISMISSED = 'mlm.cashier.install.dismissed';

function ageOf(iso: string | null): string {
  if (!iso) return 'unknown';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

const waiting = (n: number) => `${n} ${n === 1 ? 'sale' : 'sales'} waiting`;

/**
 * Tells the cashier where the till stands: offline and how many sales are
 * waiting, syncing, or needing a sign-in. Silent when all is well.
 */
export function OfflineBar() {
  const navigate = useNavigate();
  const local = useIsLocalMode();
  const online = useOfflineStore((s) => s.online);
  const pending = useOfflineStore((s) => s.pending);
  const stuck = useOfflineStore((s) => s.stuck);
  const syncing = useOfflineStore((s) => s.syncing);
  const needsSignIn = useOfflineStore((s) => s.needsSignIn);
  const packAt = useOfflineStore((s) => s.packAt);
  const installPrompt = useOfflineStore((s) => s.installPrompt);
  const [installHidden, setInstallHidden] = useState(
    () => window.localStorage.getItem(INSTALL_DISMISSED) === '1',
  );

  async function signInToSync() {
    await logout();
    navigate('/login');
  }

  const row = 'no-print flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm';

  return (
    <>
      {local && !needsSignIn && (
        <div role="status" className={cn(row, 'bg-warning/15 text-foreground')}>
          <CloudOff className="h-4 w-4 shrink-0" />
          <span className="font-medium">Offline</span>
          <span>{pending > 0 ? waiting(pending) : 'No sales waiting'}</span>
          <span className="text-muted-foreground">Prices from {ageOf(packAt)}</span>
        </div>
      )}
      {online && needsSignIn && (
        <div role="status" className={cn(row, 'bg-warning/15 text-foreground')}>
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="font-medium">Back online.</span>
          <span>Sign in to send {waiting(pending)}.</span>
          <Button size="sm" variant="outline" onClick={signInToSync}>
            Sign in
          </Button>
        </div>
      )}
      {!local && !needsSignIn && pending > 0 && stuck < pending && (
        <div role="status" className={cn(row, 'bg-primary/10 text-foreground')}>
          <RefreshCw className={cn('h-4 w-4 shrink-0', syncing && 'motion-safe:animate-spin')} />
          <span>Sending {waiting(pending)}…</span>
        </div>
      )}
      {stuck > 0 && !local && (
        <div role="alert" className={cn(row, 'bg-destructive/10 text-foreground')}>
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span>
            {stuck === 1 ? '1 record' : `${stuck} records`} could not be sent. It stays safe on this
            till. Ask your admin.
          </span>
        </div>
      )}
      {installPrompt && !installHidden && !local && (
        <div className={cn(row, 'border-b bg-background')}>
          <Download className="h-4 w-4 shrink-0" />
          <span>Install this till so it opens without internet.</span>
          <Button size="sm" onClick={() => void installPrompt.prompt()}>
            Install
          </Button>
          <button
            type="button"
            className="min-h-11 px-2 text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => {
              window.localStorage.setItem(INSTALL_DISMISSED, '1');
              setInstallHidden(true);
            }}
          >
            Not now
          </button>
        </div>
      )}
    </>
  );
}
